import React, { useState, useEffect } from "react";

import TreatmentPlanService, {
  CreateTreatmentPlanData,
} from "../../../../services/treatmentPlanService";
import { Patient } from "../../PatientCard";

interface TreatmentPlanModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onPlanCreated?: (planId: number) => void;
  appointmentId?: number;
}

interface PlanItem {
  item_type:
    | "medication"
    | "therapy"
    | "procedure"
    | "lifestyle"
    | "follow_up"
    | "lab_test"
    | "imaging";
  title: string;
  description: string;
  instructions: string;
  frequency: string;
  duration: string;
  start_date: string;
  end_date: string;
  is_critical: boolean;
  order_sequence: number;
}

const TreatmentPlanModal: React.FC<TreatmentPlanModalProps> = ({
  patient,
  isOpen,
  onClose,
  onPlanCreated,
  appointmentId,
}) => {
  const [planName, setPlanName] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [objectives, setObjectives] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [progressNotes, setProgressNotes] = useState("");
  const [planItems, setPlanItems] = useState<PlanItem[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const itemTypes = [
    { value: "medication", label: "Medication" },
    { value: "therapy", label: "Therapy" },
    { value: "procedure", label: "Procedure" },
    { value: "lifestyle", label: "Lifestyle" },
    { value: "follow_up", label: "Follow-up" },
    { value: "lab_test", label: "Lab Test" },
    { value: "imaging", label: "Imaging" },
  ];

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      // Set default start date to today
      setStartDate(new Date().toISOString().split("T")[0]);
    }
  }, [isOpen]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPlanName("");
      setDiagnosis("");
      setObjectives("");
      setStartDate("");
      setEndDate("");
      setPriority("medium");
      setProgressNotes("");
      setPlanItems([]);
      setShowTemplates(false);
      setErrors({});
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const templateData = await TreatmentPlanService.getTemplates();
      setTemplates(templateData);
    } catch (error) {
      console.error("Failed to load templates:", error);
    }
  };

  const addEmptyItem = () => {
    const newItem: PlanItem = {
      item_type: "medication",
      title: "",
      description: "",
      instructions: "",
      frequency: "",
      duration: "",
      start_date: startDate || new Date().toISOString().split("T")[0],
      end_date: "",
      is_critical: false,
      order_sequence: planItems.length + 1,
    };
    setPlanItems([...planItems, newItem]);
  };

  const updateItem = (index: number, field: keyof PlanItem, value: any) => {
    const updatedItems = [...planItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setPlanItems(updatedItems);
  };

  const removeItem = (index: number) => {
    const updatedItems = planItems.filter((_, i) => i !== index);
    // Reorder sequence numbers
    updatedItems.forEach((item, i) => {
      item.order_sequence = i + 1;
    });
    setPlanItems(updatedItems);
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === planItems.length - 1)
    ) {
      return;
    }

    const updatedItems = [...planItems];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    // Swap items
    [updatedItems[index], updatedItems[swapIndex]] = [
      updatedItems[swapIndex],
      updatedItems[index],
    ];

    // Update sequence numbers
    updatedItems.forEach((item, i) => {
      item.order_sequence = i + 1;
    });

    setPlanItems(updatedItems);
  };

  const applyTemplate = (template: any) => {
    setPlanName(template.name);
    setDiagnosis(template.description || "");
    setObjectives(`Treatment plan based on ${template.name} template`);

    const templateItems: PlanItem[] = template.items.map(
      (item: any, index: number) => ({
        item_type: item.item_type,
        title: item.title,
        description: item.description || "",
        instructions: item.instructions || "",
        frequency: item.frequency || "",
        duration: item.duration || "",
        start_date: startDate || new Date().toISOString().split("T")[0],
        end_date: "",
        is_critical: item.is_critical || false,
        order_sequence: index + 1,
      })
    );

    setPlanItems(templateItems);
    setShowTemplates(false);
  };

  const validatePlan = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!planName.trim()) {
      newErrors.planName = "Plan name is required";
    }

    if (!diagnosis.trim()) {
      newErrors.diagnosis = "Diagnosis is required";
    }

    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (endDate && endDate <= startDate) {
      newErrors.endDate = "End date must be after start date";
    }

    if (planItems.length === 0) {
      newErrors.items = "Please add at least one treatment item";
    }

    planItems.forEach((item, index) => {
      if (!item.title.trim()) {
        newErrors[`item_${index}_title`] = "Item title is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validatePlan()) return;

    setIsSaving(true);
    try {
      const planData: CreateTreatmentPlanData = {
        patient_id: patient.id,
        appointment_id: appointmentId,
        plan_name: planName,
        diagnosis,
        objectives: objectives || undefined,
        start_date: startDate,
        end_date: endDate || undefined,
        priority,
        progress_notes: progressNotes || undefined,
        items: planItems.map((item) => ({
          item_type: item.item_type,
          title: item.title,
          description: item.description || undefined,
          instructions: item.instructions || undefined,
          frequency: item.frequency || undefined,
          duration: item.duration || undefined,
          start_date: item.start_date || undefined,
          end_date: item.end_date || undefined,
          is_critical: item.is_critical,
          order_sequence: item.order_sequence,
        })),
      };

      const plan = await TreatmentPlanService.createTreatmentPlan(planData);

      if (onPlanCreated) {
        onPlanCreated(plan.id!);
      }

      onClose();
    } catch (error: any) {
      setErrors({ submit: error.message || "Failed to save treatment plan" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
    overflow: "auto",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "1200px",
    maxHeight: isMobile ? "100%" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    margin: isMobile ? "10px 0" : "auto",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px", // Add tablet support
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px", // Add consistent gap
    flexShrink: 0, // Add this to prevent shrinking
  };
  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };
  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "10px 14px" : "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
    boxSizing: "border-box",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" | "success" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#10b981", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
      success: { background: "#10b981", color: "white" },
    };

    return {
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      textAlign: "center",
    };
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px", // Add tablet support
                fontWeight: "600",
                margin: "0 0 4px 0", // Change margin format
                color: "#1f2937", // Add explicit color
              }}
            >
              New Treatment Plan
            </h2>
            <p
              style={{
                fontSize: isMobile ? "13px" : isTablet ? "13px" : "14px", // Add tablet support
                color: "#64748b",
                margin: 0,
              }}
            >
              Create treatment plan for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              style={buttonStyle("secondary")}
            >
              {showTemplates ? "Hide Templates" : "Use Template"}
            </button>
            <button
              onClick={onClose}
              style={{ ...buttonStyle("secondary"), padding: "6px 10px" }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Patient Info */}
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <strong>Patient:</strong>{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </div>
              <div>
                <strong>Age:</strong> {patient.age} years
              </div>
              <div>
                <strong>Gender:</strong> {patient.gender}
              </div>
              <div>
                <strong>Phone:</strong> {patient.phone}
              </div>
            </div>
            {patient.chronic_conditions &&
              patient.chronic_conditions.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    background: "#eff6ff",
                    border: "1px solid #dbeafe",
                    borderRadius: "6px",
                    color: "#1e40af",
                    wordBreak: "break-word",
                  }}
                >
                  <strong>Chronic Conditions:</strong>{" "}
                  {patient.chronic_conditions.join(", ")}
                </div>
              )}
          </div>

          {/* Templates Section */}
          {showTemplates && (
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "24px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                Treatment Plan Templates
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "12px",
                }}
              >
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    style={{
                      padding: "16px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#10b981";
                      e.currentTarget.style.background = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                      {template.name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginBottom: "8px",
                        wordBreak: "break-word",
                      }}
                    >
                      {template.description}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                      }}
                    >
                      {template.items.length} items • {template.category}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plan Basic Information */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Plan Information
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Plan Name *
                </label>
                <input
                  type="text"
                  id="plan_name"
                  name="plan_name"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="e.g., Post-Surgery Recovery Plan"
                  style={{
                    ...inputStyle,
                    borderColor: errors.planName ? "#ef4444" : "#e5e7eb",
                  }}
                />

                {errors.planName && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.planName}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) =>
                    setPriority(e.target.value as "low" | "medium" | "high")
                  }
                  style={inputStyle}
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Diagnosis *
              </label>
              <input
                type="text"
                id="primary_diagnosis"
                name="primary_diagnosis"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                placeholder="Primary diagnosis or condition"
                style={{
                  ...inputStyle,
                  borderColor: errors.diagnosis ? "#ef4444" : "#e5e7eb",
                }}
              />

              {errors.diagnosis && (
                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.diagnosis}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Treatment Objectives
              </label>
              <textarea
                value={objectives}
                onChange={(e) => setObjectives(e.target.value)}
                placeholder="Goals and objectives for this treatment plan..."
                rows={3}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Start Date *
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.startDate ? "#ef4444" : "#e5e7eb",
                  }}
                />

                {errors.startDate && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.startDate}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.endDate ? "#ef4444" : "#e5e7eb",
                  }}
                />

                {errors.endDate && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.endDate}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Treatment Items */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  margin: 0,
                }}
              >
                Treatment Items ({planItems.length})
              </h3>
              <button onClick={addEmptyItem} style={buttonStyle("primary")}>
                Add Item
              </button>
            </div>

            {errors.items && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "14px",
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                }}
              >
                {errors.items}
              </div>
            )}

            {planItems.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#6b7280",
                  border: "2px dashed #e5e7eb",
                  borderRadius: "8px",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>📋</div>
                <p>No treatment items added yet.</p>
                <p style={{ fontSize: "14px" }}>
                  Click "Add Item" to create treatment steps or use a template.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {planItems.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "16px",
                      background: "#fafafa",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: "#e5e7eb",
                            color: "#374151",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          #{item.order_sequence}
                        </span>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`is_critical_${index}`}
                            name={`is_critical_${index}`}
                            checked={item.is_critical}
                            onChange={(e) =>
                              updateItem(index, "is_critical", e.target.checked)
                            }
                          />

                          <span style={{ fontSize: "12px", fontWeight: "500" }}>
                            Critical
                          </span>
                        </label>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => moveItem(index, "up")}
                          disabled={index === 0}
                          style={{
                            ...buttonStyle("secondary"),
                            padding: "4px 8px",
                            fontSize: "12px",
                            opacity: index === 0 ? 0.5 : 1,
                            minWidth: "auto",
                          }}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveItem(index, "down")}
                          disabled={index === planItems.length - 1}
                          style={{
                            ...buttonStyle("secondary"),
                            padding: "4px 8px",
                            fontSize: "12px",
                            opacity: index === planItems.length - 1 ? 0.5 : 1,
                            minWidth: "auto",
                          }}
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeItem(index)}
                          style={{
                            ...buttonStyle("danger"),
                            padding: "4px 8px",
                            fontSize: "12px",
                            minWidth: "auto",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "auto 2fr",
                        gap: "16px",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Type
                        </label>
                        <select
                          value={item.item_type}
                          onChange={(e) =>
                            updateItem(index, "item_type", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        >
                          {itemTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Title *
                        </label>
                        <input
                          type="text"
                          id={`item_title_${index}`}
                          name={`item_title_${index}`}
                          value={item.title}
                          onChange={(e) =>
                            updateItem(index, "title", e.target.value)
                          }
                          placeholder="e.g., Take medication, Physical therapy session"
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                            borderColor: errors[`item_${index}_title`]
                              ? "#ef4444"
                              : "#e5e7eb",
                          }}
                        />

                        {errors[`item_${index}_title`] && (
                          <div
                            style={{
                              color: "#ef4444",
                              fontSize: "10px",
                              marginTop: "2px",
                            }}
                          >
                            {errors[`item_${index}_title`]}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Frequency
                        </label>
                        <input
                          type="text"
                          id={`item_frequency_${index}`}
                          name={`item_frequency_${index}`}
                          value={item.frequency}
                          onChange={(e) =>
                            updateItem(index, "frequency", e.target.value)
                          }
                          placeholder="e.g., Daily, Twice daily"
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Duration
                        </label>
                        <input
                          type="text"
                          id={`item_duration_${index}`}
                          name={`item_duration_${index}`}
                          value={item.duration}
                          onChange={(e) =>
                            updateItem(index, "duration", e.target.value)
                          }
                          placeholder="e.g., 2 weeks, 1 month"
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          Start Date
                        </label>
                        <input
                          type="date"
                          id={`item_start_date_${index}`}
                          name={`item_start_date_${index}`}
                          value={item.start_date}
                          onChange={(e) =>
                            updateItem(index, "start_date", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "500",
                            marginBottom: "4px",
                          }}
                        >
                          End Date
                        </label>
                        <input
                          type="date"
                          id={`item_end_date_${index}`}
                          name={`item_emd_date_${index}`}
                          value={item.start_date}
                          onChange={(e) =>
                            updateItem(index, "start_date", e.target.value)
                          }
                          style={{
                            ...inputStyle,
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: "12px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Description
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) =>
                          updateItem(index, "description", e.target.value)
                        }
                        placeholder="Detailed description of this treatment step..."
                        rows={2}
                        style={{
                          ...inputStyle,
                          fontSize: "12px",
                          padding: "8px 12px",
                          resize: "none",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Instructions
                      </label>
                      <textarea
                        value={item.instructions}
                        onChange={(e) =>
                          updateItem(index, "instructions", e.target.value)
                        }
                        placeholder="Specific instructions for the patient..."
                        rows={2}
                        style={{
                          ...inputStyle,
                          fontSize: "12px",
                          padding: "8px 12px",
                          resize: "none",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress Notes */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Initial Notes
            </h3>
            <textarea
              value={progressNotes}
              onChange={(e) => setProgressNotes(e.target.value)}
              placeholder="Initial notes about the treatment plan, patient condition, or special considerations..."
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
          </div>

          {errors.submit && (
            <div
              style={{
                color: "#ef4444",
                fontSize: "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Footer */}
          <div style={footerStyle}>
            <div
              style={{
                fontSize: isMobile ? "13px" : "13px",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              {planItems.length > 0 && (
                <>
                  {planItems.length} treatment item(s) •
                  {planItems.filter((item) => item.is_critical).length} critical
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                {isSaving ? "💾 Saving..." : "💾 Create Treatment Plan"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
         input:focus, select:focus, textarea:focus {
           border-color: #10b981 !important;
           box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
         }
         
         button:hover:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
         }
         
         @media (max-width: 768px) {
           .mobile-stack {
             flex-direction: column !important;
           }
           
           .mobile-full-width {
             width: 100% !important;
           }
           
           .mobile-grid-single {
             grid-template-columns: 1fr !important;
           }
           
           .mobile-text-left {
             text-align: left !important;
           }
         }
         
         @media (max-width: 480px) {
           .mobile-sm-stack {
             flex-direction: column !important;
           }
           
           .mobile-sm-center {
             justify-content: center !important;
           }
         }
       `}
      </style>
    </div>
  );
};

export default TreatmentPlanModal;
